import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/blog`);
      setPosts(response.data);
    } catch (error) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Learning Smart Blog
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Tips, insights, and inspiration for parents and educators
          </p>
          <Card className="max-w-md mx-auto">
            <CardContent className="p-12 text-gray-500">
              <p className="text-lg">Coming Soon!</p>
              <p className="text-sm mt-2">We're working on great content for you.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Learning Smart Blog
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tips, insights, and inspiration for parents and educators
          </p>
        </div>

        {/* Featured Post */}
        <Card className="mb-12 overflow-hidden hover:shadow-2xl transition-shadow" data-testid="featured-post">
          <div className="grid md:grid-cols-2 gap-0">
            {featured.image ? (
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover min-h-[250px]"
              />
            ) : (
              <div className="w-full h-full min-h-[250px] bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-6xl font-bold opacity-30">{featured.title[0]}</span>
              </div>
            )}
            <CardContent className="p-8 flex flex-col justify-center">
              <div className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full mb-4 w-fit">
                {featured.category}
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                {featured.title}
              </h2>
              <p className="text-gray-600 mb-6">{featured.excerpt}</p>
              <div className="flex items-center text-sm text-gray-500 mb-6 space-x-4">
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(featured.createdAt || featured.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {featured.readTime || featured.read_time}
                </span>
              </div>
              <Button asChild className="w-fit">
                <Link to={`/blog/${featured.id}`}>
                  Read More <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </div>
        </Card>

        {/* Blog Grid */}
        {rest.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((post) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow group" data-testid={`blog-card-${post.id}`}>
                <div className="relative overflow-hidden">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white text-4xl font-bold opacity-30">{post.title[0]}</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    {post.category}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center text-xs text-gray-500 mb-4 space-x-3">
                    <span className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(post.createdAt || post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {post.readTime || post.read_time}
                    </span>
                  </div>
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to={`/blog/${post.id}`}>
                      Read Article
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
